import * as t from './types'
import React from 'react'
import * as helpers from './utils/helperFunctions'
import styled from 'styled-components'
import theme from 'theme'
import withTranslations from './withTranslations'
import useTranslations from 'hooks/useTranslations'
import B2cSwitch from 'theme/atoms/B2cSwitch'
import calcPrices from './utils/calcPrices'
import priceStr from 'utils/priceString'
import { useDisplayVariant } from 'modules/productDetail'
import DeliveryBadge from 'theme/molecules/Price/DeliveryBadge'
import { useCustomTailor } from 'modules/productDetail'

export type Props = {
  'data-cy-state'?: string
  prices: t.ProductPriceDefinition
  unit?: { unitName: string; purchaseUnit: number; packUnit: string }
  logic: string
  options?: {
    isFlyoutCart?: boolean
    pageType?: t.PageType
    exclTax?: boolean
    amount?: number
    customTailor?: boolean
    showdeliveryBadge?: boolean
    hideEcoTax?: boolean
    hideStrikePrice?: boolean
    recentFlyoutArticle?: boolean
    renderDeliveryBadgeIfNotAvailable?: boolean
    showSubline?: boolean
    specialType?:
      | 'HighlightProduct'
      | 'ProductSlider'
      | 'HotspotTeaser'
      | 'SeriesWidget'
      | 'CustomTailorProduct'
  }
}
function Price(props: Props) {
  const { prices, logic, unit, options } = props
  const {
    inclTaxPDP,
    inclTaxListing,
    getPageType,
    customTailorPrice,
    inclEcoTax,
    inclSeriesWidget,
    reductionNumber
  } = helpers

  const price = calcPrices(prices)
  const net = price.net[logic]
  const gross = price.gross[logic]
  const variant = useDisplayVariant()
  const isEcoTax = !options?.hideEcoTax
  const hideStrikePrice = options?.hideStrikePrice || false
  const renderDeliveryBadgeIfNotAvailable =
    typeof options?.renderDeliveryBadgeIfNotAvailable === 'undefined'
      ? true
      : options.renderDeliveryBadgeIfNotAvailable

  const showSubline =
    typeof options?.showSubline === 'undefined' ? true : options.showSubline

  const customTailor = useCustomTailor()
  const isCustomTailorConfigured =
    (customTailor.data.brink &&
      customTailor.data.shape &&
      customTailor.data.size) ||
    false

  const t = useTranslations<'prices'>()

  const strikePrice = price.sku_has_pseudo_price || false
  const from = price.product_has_price_range
  const reduction = price.reducedPrice || false
  const page = getPageType(options?.pageType)
  const amount = page === 'Product' ? 1 : options?.amount || 1
  return (
    <Wrapper
      className="Prices"
      data-cy-ctx="molecules/Price"
      $StrikePrice={strikePrice}
      $isFlyout={options?.isFlyoutCart}
      $RecentFlyoutArticle={options?.recentFlyoutArticle}
      $Page={page}
      $showSubline={showSubline}
      $logic={logic}
      $specialType={options?.specialType}
      data-cy-state={props['data-cy-state']}
    >
      <div className="topline">
        {(((page === 'Listing' || options?.specialType === 'ProductSlider') &&
          from) ||
          options?.specialType === 'SeriesWidget') && (
          <div data-cy-state="from" className="from">
            {t.asText(false, 'from') + ' '}
          </div>
        )}
        <B2cSwitch.B2B>
          <div className="main-wrapper">
            {strikePrice &&
              (page === 'Cart' ||
                options?.specialType === 'HighlightProduct') &&
              !hideStrikePrice && (
                <StrikePrice
                  data-cy-state="strike-price"
                  $Page={page}
                  $isFlyout={options?.isFlyoutCart}
                  $RecentFlyoutArticle={options?.recentFlyoutArticle}
                >
                  {priceStr(net.PseudoPrice * amount)}
                </StrikePrice>
              )}
            <NormalPrice
              data-cy-state="normal-price"
              $StrikePrice={strikePrice}
              $Page={page}
              $specialType={options?.specialType}
            >
              {options?.customTailor
                ? customTailorPrice(net.Price, variant, customTailor)
                : page === 'Listing' && net.productCheapestPrice
                  ? priceStr(net.productCheapestPrice)
                  : priceStr(net.Price * amount)}
            </NormalPrice>

            {strikePrice &&
              page !== 'Cart' &&
              options?.specialType !== 'HighlightProduct' && (
                <StrikePrice
                  data-cy-state="strike-price"
                  $Page={page}
                  $isFlyout={options?.isFlyoutCart}
                  $RecentFlyoutArticle={options?.recentFlyoutArticle}
                >
                  {page === 'Listing' && from
                    ? ' ' + priceStr(net.productCheapestPseudoPrice * amount)
                    : ' ' + priceStr(net.PseudoPrice * amount)}
                </StrikePrice>
              )}
          </div>
        </B2cSwitch.B2B>
        <B2cSwitch.B2C>
          <div className="main-wrapper">
            {strikePrice &&
              (page === 'Cart' ||
                options?.specialType === 'HighlightProduct') &&
              !hideStrikePrice && (
                <StrikePrice
                  data-cy-state="strike-price"
                  $Page={page}
                  $isFlyout={options?.isFlyoutCart}
                  $RecentFlyoutArticle={options?.recentFlyoutArticle}
                >
                  {priceStr(gross.PseudoPrice * amount)}
                </StrikePrice>
              )}
            <NormalPrice
              data-cy-state="normal-price"
              $StrikePrice={strikePrice}
              $Page={page}
              $specialType={options?.specialType}
            >
              {options?.customTailor
                ? customTailorPrice(gross.Price, variant, customTailor)
                : page === 'Listing' && net.productCheapestPrice
                  ? priceStr(gross.productCheapestPrice)
                  : priceStr(gross.Price * amount)}
            </NormalPrice>

            {strikePrice &&
              page !== 'Cart' &&
              options?.specialType !== 'HighlightProduct' && (
                <StrikePrice
                  data-cy-state="strike-price"
                  $Page={page}
                  $isFlyout={options?.isFlyoutCart}
                  $RecentFlyoutArticle={options?.recentFlyoutArticle}
                >
                  {page === 'Listing' && from
                    ? ' ' + priceStr(gross.productCheapestPseudoPrice * amount)
                    : ' ' + priceStr(gross.PseudoPrice * amount)}
                </StrikePrice>
              )}
          </div>
        </B2cSwitch.B2C>

        {page === 'Product' && reduction && (
          <ReductionPrice data-cy-state="reduction-price">
            {reductionNumber(price, logic)}
          </ReductionPrice>
        )}
      </div>

      {showSubline && (
        <div className="subline" data-cy-state="show-subline">
          {options?.customTailor && !isCustomTailorConfigured ? (
            <div className="bottomline" data-cy-state="squaremeter-text">
              {t.asText(false, 'squaremeter_price')}
            </div>
          ) : options?.isFlyoutCart &&
            options?.specialType !== 'ProductSlider' ? (
            <div className="bottomline" data-cy-state="flyoutcart-bottomline">
              {t.asText(false, 'flyoutcart_unit', {
                UNIT: unit?.packUnit || ''
              })}
            </div>
          ) : (
            <div className="bottomline">
              {page === 'Product' &&
                options?.exclTax &&
                ' ' + t.asText(false, 'excl_tax')}
              {page === 'Product' &&
                (options?.customTailor
                  ? inclTaxPDP(
                      customTailorPrice(gross.Price, variant, customTailor),
                      logic,
                      unit
                    )
                  : inclTaxPDP(gross.Price * amount, logic, unit, price))}
              {(page === 'Listing' || page === 'Other') &&
                (options?.specialType === 'SeriesWidget'
                  ? inclSeriesWidget(
                      unit,
                      price.net.referencePriceString,
                      price.gross.referencePriceString
                    )
                  : inclTaxListing(
                      unit,
                      net,
                      gross,
                      from,
                      price,
                      page,
                      options?.specialType
                    ))}
              {page === 'Series' &&
                inclTaxPDP(
                  gross.Price,
                  logic === 'piece' ? 'series' : 'pack',
                  unit
                )}
              {(price.net.ecoTax || price.gross.ecoTax) &&
                isEcoTax &&
                page !== 'Listing' &&
                page !== 'Other' &&
                !options?.isFlyoutCart &&
                inclEcoTax(price, amount || 1, page)}
            </div>
          )}
          {options?.showdeliveryBadge && (
            <div
              className="delivery-badge-wrapper"
              data-cy-state="delivery-badge-possible"
            >
              <DeliveryBadge
                amount={options?.amount || 0}
                variant={variant}
                renderDeliveryBadgeIfNotAvailable={
                  renderDeliveryBadgeIfNotAvailable
                }
              />
            </div>
          )}
        </div>
      )}
    </Wrapper>
  )
}

export default withTranslations(Price)

const Wrapper = styled.div<{
  $StrikePrice
  $Page
  $isFlyout
  $RecentFlyoutArticle
  $showSubline
  $logic
  $specialType
}>`
  ${(p) =>
    p.$specialType === 'HotspotTeaser' && `margin-top: ${theme.spacing('s')};`}
  ${(p) =>
    (p.$Page === 'Series' || !p.$RecentFlyoutArticle) &&
    `display: flex; gap: ${theme.spacing('xxs')}; align-items: baseline;`}
  ${(p) =>
    p.$Page === 'Product' &&
    `margin-bottom: ${theme.spacing('s')}; flex-direction: column; gap: 0;`}
    ${(p) =>
    p.$showSubline === false && `margin-bottom: ${theme.spacing('xxs')};`}
    @media (min-width: ${theme.ms.LG}px) {
    ${(p) => p.$Page === 'Series' && `display: block;`}
  }
  ${(p) => (p.$Page === 'Listing' || p.$Page === 'Other') && `display: block;`}

  ${(p) => p.$Page === 'Cart' && `flex-direction: column;`}
    @media (min-width: ${theme.ms.SM}px) {
    ${(p) => p.$Page === 'Cart' && ` align-items: end;`}
  }
  ${(p) => p.$specialType === 'CustomTailorProduct' && `margin-bottom: 0;`}
  .topline {
    display: flex;
    gap: ${theme.spacing('xxs')};
    align-items: baseline;

    @media (min-width: ${theme.ms.SM}px) {
      ${(p) => p.$Page === 'Cart' && `justify-content: flex-end;`}
    }

    ${(p) =>
      p.$Page === 'Product' &&
      `align-itmes: start; gap: ${theme.spacing('s')};`}
    ${(p) =>
      p.$Page === 'Series' && `${theme.ty('r-s', '700', 'r-base', 'r-2xl')};`}
      ${(p) =>
      p.$Page === 'Other' &&
      p.$specialType === 'HighlightProduct' &&
      `${theme.ty('r-l', '700', 'r-2xl')} `}
      ${(p) =>
      p.$Page === 'Other' &&
      p.$specialType === 'ProductSlider' &&
      (p.$isFlyout
        ? `${theme.ty('r-base', '700', 'r-l')}`
        : `${theme.ty('r-s', '700', 'r-base')}`)}
      ${(p) =>
      p.$Page === 'Other' &&
      p.$specialType === 'HotspotTeaser' &&
      `color: ${theme.colors.shade.b2}; ${theme.ty('r-s', '700', 'r-base')}`}


    .from {
      ${(p) => p.$StrikePrice && `color: ${theme.colors.accent.red};`}
      ${(p) => p.$Page === 'Listing' && `${theme.ty('r-s', '700', 'r-base')}`}
      ${(p) =>
        p.$specialType === 'SeriesWidget' &&
        `${theme.ty('r-xs', '700', 'r-base')}`}
    }
    .to {
      ${(p) => p.$StrikePrice && `color: ${theme.colors.accent.red};`}
    }
    > span > .main-wrapper {
      align-items: baseline;
      display: flex;
      gap: ${theme.spacing('xxs')};
      ${(p) => p.$isFlyout && `gap: ${theme.spacing('xs')};`}
      ${(p) => p.$Page === 'Product' && `gap: ${theme.spacing('s')};`}
      ${(p) =>
        p.$Page === 'Other' &&
        p.$specialType === 'HighlightProduct' &&
        `gap: ${theme.spacing('xs')};`}

      > span {
        ${(p) =>
          p.$Page === 'Other' &&
          p.$specialType === 'HotspotTeaser' &&
          `color: ${theme.colors.shade.b2};`}
        ${(p) =>
          p.$specialType === 'CustomTailorProduct' &&
          `${theme.ty('r-s', 'bold', 'r-base')}`}
      }
    }
  }
  .subline {
    display: flex;
    justify-content: space-between;
    column-gap: ${theme.spacing('s')};
    ${(p) =>
      p.$Page === 'Product' &&
      `min-height: ${theme.spacing('l')}; width: 100%;`}
    ${(p) => p.$Page === 'Listing' && `display: block;`}
    ${(p) => p.$Page === 'Cart' && `width: 100%;`}
    .bottomline {
      display: flex;
      flex-direction: column;
      ${(p) =>
        p.$Page === 'Product' && `min-height: 2.8125rem; flex-basis: 50%;`}
      ${(p) =>
        p.$Page === 'Product' && p.$logic === 'pack' && `flex-basis: 100%;`}
      ${(p) =>
        p.$isFlyout && `color: ${theme.colors.shade.b2}; white-space: nowrap;`}
      ${theme.ty('r-s', '400')}
      ${(p) =>
        p.$isFlyout && !p.$RecentFlyoutArticle && `${theme.ty('r-xs', '400')}`}
      
      ${(p) =>
        p.$Page === 'Other' &&
        p.$specialType === 'HighlightProduct' &&
        theme.ty('r-s', '400', 'r-base')}
      ${(p) =>
        p.$Page === 'Other' &&
        p.$specialType === 'ProductSlider' &&
        theme.ty('r-xs', '400', 'r-s')}
        ${(p) =>
        p.$Page === 'Other' &&
        p.$specialType === 'HotspotTeaser' &&
        `color: ${theme.colors.shade.b2}; ${theme.ty('r-xs', '700')}`}
/*  */
      @media (min-width: ${theme.ms.LG}px) {
        ${(p) => p.$Page === 'Series' && `white-space: normal;`}
      }

      > div {
        ${(p) =>
          p.$Page === 'Series' &&
          `color: ${theme.colors.shade.b2}; ${theme.ty('r-xs', '400', 'r-s', 'r-base')}`}
      }
    }
    .delivery-badge-wrapper {
      display: flex;
      flex-basis: 50%;
      justify-content: flex-end;
    }
  }
`

const StrikePrice = styled.span<{
  $Page
  $isFlyout
  $RecentFlyoutArticle
}>`
  text-decoration: line-through;
  color: ${theme.colors.shade.b2};
  ${theme.ty('r-s', '400', 'r-base')}
  ${(p) => p.$Page === 'Cart' && `${theme.ty('r-s', '400', 'r-base')}`}
  ${(p) => p.$Page === 'Other' && `${theme.ty('r-xs', '400', 'r-base')}`}
  ${(p) =>
    p.$Page === 'Product' && `align-self: end; ${theme.ty('r-base', '400')};`}
  ${(p) =>
    p.$isFlyout &&
    !p.$RecentFlyoutArticle &&
    `${theme.ty('r-xs', '400', 'r-xs')}`}
`

const NormalPrice = styled.span<{ $StrikePrice; $Page; $specialType }>`
  color: ${theme.colors.b0};
  ${(p) => p.$StrikePrice && ` color: ${theme.colors.accent.red};`}

  ${(p) => p.$Page === 'Product' && `${theme.ty('rc-2xl', '700')};`}
  ${(p) =>
    (p.$Page === 'Listing' || p.$Page === 'Other') &&
    `${theme.ty('r-s', 'bold', 'r-base')};`}
    ${(p) =>
    p.$specialType === 'HighlightProduct' && theme.ty('r-l', '700', 'r-2xl')}
`

const ReductionPrice = styled.span`
  align-self: end;
  color: ${theme.colors.accent.red};
  ${theme.ty('rc-base', '700')}
`
